import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar, Upload, Download, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useGoogleCalendarIntegration } from '@/lib/integrations/google-calendar-integration';
import { useMultiYearHolidays } from '@/hooks/scheduling/useHolidays';
import { useBusinessTime } from '@/hooks/ui/useBusinessTime';
import { DayShift } from '@/types';
import HolidayCacheManager from './HolidayCacheManager';

interface GoogleCalendarIntegrationTabProps {
  businessId: string;
  businessName: string;
  countryCode?: string | null;
  shifts: DayShift[];
  onShiftCreated?: () => void;
}

interface CalendarConfig {
  id: string;
  calendar_id: string;
  business_id: string;
  sync_enabled: boolean;
  time_zone: string;
  last_sync: string | null;
  created_at: string;
}

export default function GoogleCalendarIntegrationTab({
  businessId,
  businessName,
  countryCode,
  shifts,
  onShiftCreated
}: GoogleCalendarIntegrationTabProps) {
  const [calendarConfig, setCalendarConfig] = useState<CalendarConfig | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [syncEnabled, setSyncEnabled] = useState(false);
  // Use centralized business timezone instead of browser timezone
  const { businessTimezone: timeZone } = useBusinessTime();
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState<Array<{ shift: DayShift; holiday: unknown; severity: 'warning' | 'error' }>>([]);
  const googleCalendar = useGoogleCalendarIntegration();

  // Get holidays for conflict detection
  const currentYear = new Date().getFullYear();
  const { holidaysByYear, isHoliday } = useMultiYearHolidays({
    countryCode,
    years: [currentYear - 1, currentYear, currentYear + 1],
    enabled: !!countryCode
  });

  // Load existing calendar configuration
  const loadCalendarConfig = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('google_calendar_configs')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setCalendarConfig(data);
        setCalendarId(data.calendar_id);
        setSyncEnabled(data.sync_enabled);
        // Note: timezone is now managed centrally through useBusinessTime
      }
    } catch (error) {
      console.error('Error loading calendar config:', error);
    }
  }, [businessId]);

  // Check for holiday conflicts function
  const checkHolidayConflicts = useCallback(async () => {
    if (!countryCode) return;

    try {
      const allHolidays = Object.values(holidaysByYear).flat();
      const detectedConflicts = await googleCalendar.checkHolidayConflicts(
        shifts,
        allHolidays,
        businessId
      );
      setConflicts(detectedConflicts);
    } catch (error) {
      console.error('Error checking holiday conflicts:', error);
    }
  }, [countryCode, holidaysByYear, shifts, businessId, googleCalendar]);

  useEffect(() => {
    loadCalendarConfig();
  }, [loadCalendarConfig]);

  // Check for holiday conflicts when shifts or holidays change
  useEffect(() => {
    if (shifts.length > 0 && countryCode) {
      checkHolidayConflicts();
    }
  }, [shifts, holidaysByYear, countryCode, checkHolidayConflicts]);


  const saveCalendarConfig = async () => {
    if (!calendarId.trim()) {
      toast.error("Error", {
        description: "Please enter a valid Calendar ID",
      });
      return;
    }

    setLoading(true);
    try {
      const configData = {
        business_id: businessId,
        calendar_id: calendarId.trim(),
        sync_enabled: syncEnabled,
        time_zone: timeZone,
        last_sync: null
      };

      const { data, error } = await supabase
        .from('google_calendar_configs')
        .upsert(configData, { onConflict: 'business_id' })
        .select()
        .single();

      if (error) throw error;

      setCalendarConfig(data);
      toast.success("Success", {
        description: "Google Calendar configuration saved successfully",
      });
    } catch (error) {
      console.error('Error saving calendar config:', error);
      toast.error("Error", {
        description: "Failed to save calendar configuration",
      });
    } finally {
      setLoading(false);
    }
  };

  const createBusinessCalendar = async () => {
    setLoading(true);
    try {
      const newCalendarId = await googleCalendar.createBusinessCalendar(businessName);
      if (newCalendarId) {
        setCalendarId(newCalendarId);
        toast.success("Success", {
          description: "Business calendar created successfully",
        });
      } else {
        throw new Error('Failed to create calendar');
      }
    } catch (error) {
      console.error('Error creating business calendar:', error);
      toast.error("Error", {
        description: "Failed to create business calendar",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportShifts = async () => {
    if (!calendarId || !syncEnabled) return;

    setLoading(true);
    try {
      const currentMonth = new Date();
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(addMonths(currentMonth, 2)); // Export 3 months

      const monthlyShifts = shifts.filter(shift => {
        const shiftDate = new Date(shift.startTime);
        return shiftDate >= startDate && shiftDate <= endDate;
      });

      const result = await googleCalendar.bulkExportShifts(
        monthlyShifts,
        calendarId,
        businessName,
        timeZone
      );

      // Update last sync time
      await supabase
        .from('google_calendar_configs')
        .update({ last_sync: new Date().toISOString() })
        .eq('business_id', businessId);

      await loadCalendarConfig();

      toast.success("Export Complete", {
        description: `Exported ${result.exported} shifts, ${result.failed} failed`,
      });
    } catch (error) {
      console.error('Error exporting shifts:', error);
      toast.error("Error", {
        description: "Failed to export shifts to Google Calendar",
      });
    } finally {
      setLoading(false);
    }
  };

  const importEvents = async () => {
    if (!calendarId || !syncEnabled) return;

    setLoading(true);
    try {
      const currentMonth = new Date();
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(addMonths(currentMonth, 1)); // Import 2 months

      const importedShifts = await googleCalendar.importCalendarEventsAsShifts(
        calendarId,
        startDate,
        endDate,
        businessId
      );

      toast.success("Import Complete", {
        description: `Found ${importedShifts.length} calendar events that could be imported as shifts`,
      });

      if (onShiftCreated) {
        onShiftCreated();
      }
    } catch (error) {
      console.error('Error importing events:', error);
      toast.error("Error", {
        description: "Failed to import events from Google Calendar",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Configuration Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Google Calendar Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="calendar-id">Calendar ID</Label>
            <div className="flex gap-2">
              <Input
                id="calendar-id"
                placeholder="your-calendar-id@gmail.com"
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
              />
              <Button onClick={createBusinessCalendar} disabled={loading}>
                Create New
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Enter your Google Calendar ID or create a new business calendar
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Time Zone</Label>
            <Input
              id="timezone"
              value={timeZone}
              readOnly
              placeholder="Timezone is managed through business settings"
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Time zone is automatically set from your business timezone settings.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="sync-enabled"
              checked={syncEnabled}
              onCheckedChange={setSyncEnabled}
            />
            <Label htmlFor="sync-enabled">Enable synchronization</Label>
          </div>

          <Button onClick={saveCalendarConfig} disabled={loading}>
            Save Configuration
          </Button>
        </CardContent>
      </Card>

      {/* Sync Actions Card */}
      {calendarConfig && syncEnabled && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Sync Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Last Sync</p>
                <p className="text-sm text-muted-foreground">
                  {calendarConfig.last_sync
                    ? format(new Date(calendarConfig.last_sync), 'PPp')
                    : 'Never'
                  }
                </p>
              </div>
              <Badge variant={syncEnabled ? "default" : "secondary"}>
                {syncEnabled ? "Active" : "Disabled"}
              </Badge>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Upload className="w-4 h-4" />
                    <span className="font-medium">Export to Google Calendar</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Export your shifts to Google Calendar
                  </p>
                  <Button onClick={exportShifts} disabled={loading} size="sm">
                    Export Shifts
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Download className="w-4 h-4" />
                    <span className="font-medium">Import from Google Calendar</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Import calendar events as shifts
                  </p>
                  <Button onClick={importEvents} disabled={loading} size="sm">
                    Import Events
                  </Button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Holiday Conflicts Card */}
      {conflicts.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Holiday Conflicts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conflicts.map((conflict, index) => (
                <Alert key={index} variant={conflict.severity === 'error' ? 'destructive' : 'default'}>
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {conflict.shift.title || 'Shift'} on {format(conflict.shift.startTime, 'PPP')}
                        </p>
                        <p className="text-sm">
                          Conflicts with {conflict.holiday.name} ({conflict.holiday.type})
                        </p>
                      </div>
                      <Badge variant={conflict.severity === 'error' ? 'destructive' : 'secondary'}>
                        {conflict.severity}
                      </Badge>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Holiday Cache Management */}
      <HolidayCacheManager countryCode={countryCode} />

      {/* Help Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium">1. Get your Calendar ID</h4>
            <p className="text-sm text-muted-foreground">
              Go to Google Calendar → Settings → Select calendar → Calendar ID
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium">2. Enable API Access</h4>
            <p className="text-sm text-muted-foreground">
              Make sure your calendar is public or shared with the service account
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium">3. Configure Sync</h4>
            <p className="text-sm text-muted-foreground">
              Enable synchronization and set your preferred time zone
            </p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href="https://developers.google.com/calendar/api/guides/overview" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-2" />
              Google Calendar API Docs
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}